import { NavigationSection } from '@app/context/constants';
import { useUser } from '@app/context/UserContext';
import { useHealthCheck } from '@app/hooks/useHealthCheck';
import { useNavigation } from '@app/hooks/useNavigation';
import { useQueryParams } from '@app/hooks/useQuestionaireRoute';
import { Header, Navigation } from '@components/block';
import { TextReplacer } from '@components/ui/TextReplacer/TextReplacer';
import { HeaderSkeleton, Loading, NavigationSkeleton } from '@components/ui';
import { useGetShareTokenJWTQuery } from '@dieterApi/sharetoken/useGetShareTokenJWTQuery';
import { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';

import { useSnackbar } from '@app/hooks/useSnackbar';
import { Locales } from '@components/block/Header/LangSelect';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { ChatWidget } from '@components/ui/ChatWidget';
import { useUserLoginMutation } from '@dieterApi/user/useUserLoginMutation';
import { useTranslation } from 'react-i18next';
import { Redirect, RouteProps, Switch, useHistory, useLocation } from 'react-router-dom';
import Admin from './Admin/Admin';
import Dashboard from './Dashboard/Dashboard';
import DocumentEmbed from './DocumentEmbed/DocumentEmbed';
import DocumentShare from './DocumentShare/DocumentShare';
import Dpo from './Dpo/Dpo';
import { useCompanyDeeplink } from './CompanyDeeplink';
import Error from './Error/Error';
import { Unavailable } from './Error/Unavailable';
import FileShare from './FileShare/FileShare';
import Glossary from './Glossary/Glossary';
import { Login } from './Login/Login';
import { Onboarding } from './Onboarding/Onboarding';
import { Pricing } from './Pricing/Pricing';
import { Print } from './Print/Print';
import Questionnaire from './Questionnaire/Questionnaire';
import { RegisterTenant } from './RegisterTenant/RegisterTenant';
import { TenantRegistrations } from './TenantRegistrations/TenantRegistrations';
import { Terms } from './Terms/Terms';
import Confirmation from './UserActions/Confirmation/Confirmation';
import { MFASetup } from './UserActions/MFA/MFASetup';
import { MFAVerify } from './UserActions/MFA/MFAVerify';
import Password from './UserActions/Password/Password';
import ResetPassword from './UserActions/ResetPassword/ResetPassword';
import { UserConfiguration } from './UserConfiguration/UserConfiguration';
import { UserManagement } from './UserManagement/UserManagement';
import { KlausLink } from './KlausLink/KlausLink';
import { KlausHandoff } from './KlausHandoff/KlausHandoff';

import { CelloAttribution } from '@components/ui/Cello/CelloAttribution';
import { ConversionTracker } from '@components/ui/ConversionTracker/ConversionTracker';

import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useUserLoginMittwaldMutation } from '@dieterApi/user/useUserLoginMittwaldMutation';
import { useUserLogoutMutation } from '@dieterApi/user/useUserLogoutMutation';
import { AuditAgent } from './AuditAgent/AuditAgent';
import { ChatTest } from './ChatTest/ChatTest';
import { Dashboardv2 } from './Dashboardv2/Dashboardv2';
import DocumentSign from './DocumentShare/DocumentSign';
import SignedDocumentDownload from './DocumentShare/SignedDocumentDownload';
import './route.sass';
import { Stats } from './Stats/Stats';
import { WhitelabelConfigForm } from './Whitelabel/WhitelabelConfiguration';
import { ScanResultPrint } from './ScanResultPrint/ScanResultPrint';
import { ScanResultReport } from './ScanResultReport/ScanResultReport';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;

    _paq?: Array<unknown>;
  }
}

function RootRedirect() {
  const { navigateDashboard } = useNavigation();
  const history = useHistory();

  useEffect(() => {
    // Only redirect if we're at the root path
    if (history.location.pathname === '/') {
      navigateDashboard(NavigationSection.Overview);
    }
  }, []);

  return null;
}

interface UCEvent extends Event {
  detail: {
    event: string;
    [key: string]: string | boolean;
  };
}

export function Root() {
  const location = useLocation();
  const history = useHistory();
  const { user, userLoading, whitelabelLoading, questionnaires } = useUserWithCompany();
  const { navigation, setNavigation } = useNavigation();
  const isHealthy = useHealthCheck();
  const loading = userLoading || navigation.questionnaireWrapup;
  const [showHeader, setShowHeader] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);

  useEffect(() => {
    if (navigation.isV2OnboardingMode && navigation.scope !== 'dashboardv2' && location.pathname !== '/pricing') {
      console.log(navigation.scope, location.pathname);
      setNavigation((nav) => void (nav.scope = 'dashboardv2'));
      history.push('/v2/audit');
    }
  }, [user, navigation]);

  // manage consent state in navigation context
  useEffect(() => {
    window.addEventListener('ucEvent', function (e: Event) {
      const ucEvent = e as UCEvent;
      if (ucEvent.detail && ucEvent.detail.event == 'consent_status') {
        setNavigation((nav) => void (nav.consent.hubspot = ucEvent.detail['HubSpot'] as boolean));
        setNavigation((nav) => void (nav.consent.sentry = ucEvent.detail['Sentry'] as boolean));
      }
    });
  }, []);

  useEffect(() => {
    // Only show header/navigation when not loading to prevent flash
    const shouldShowHeader =
      !location.pathname.includes('/embed') &&
      !location.pathname.startsWith('/register/') &&
      navigation.scope !== 'dashboardv2';
    const shouldShowNavigation =
      navigation.scope === 'quest' || navigation.scope === 'dashboard' || navigation.scope === 'quest_external';

    // Wait for whitelabel config to load before showing header to prevent color flash
    if (!whitelabelLoading) {
      setShowHeader(shouldShowHeader);
      setShowNavigation(shouldShowNavigation);
    }
  }, [navigation.scope, whitelabelLoading, location.pathname]);

  return (
    // The global TextReplacer renames Dieter/Klaus/simply Legal in every text
    // node of the document for a whitelabel tenant, portals included.
    <TextReplacer global>
      <ConversionTracker />
      <Unavailable open={!isHealthy} />
      {showHeader &&
        (userLoading || whitelabelLoading ? (
          <>
            <HeaderSkeleton />
            <NavigationSkeleton />
          </>
        ) : (
          <ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag('location', 'header');
            }}
          >
            <Header />
          </ErrorBoundary>
        ))}
      <ErrorBoundary
        fallback={Error}
        beforeCapture={(scope) => {
          scope.setTag('location', 'main');
        }}
      >
        {showNavigation && <Navigation />}
        {loading || whitelabelLoading ? (
          <>
            <div className="flex items-center justify-center mt-24">{/* <Loading /> */}</div>
          </>
        ) : (
          // Finally get to the app
          // <Suspense fallback={<Loading />}>
          <Switch>
            {/* Host gated but always declared so route structure stays stable */}
            <PrivateRoute hostOnly path="/v2">
              <Dashboardv2 />
            </PrivateRoute>
            <PrivateRoute hostOnly path="/audit/:applicationId?">
              <AuditAgent />
            </PrivateRoute>
            <HostRoute path="/pricing">
              <Pricing />
            </HostRoute>
            <Route path="/onboarding/:appTitleUrl?">
              <Onboarding />
            </Route>
            <Route path="/reset">
              <ResetPassword />
            </Route>
            <Route path="/confirm">
              <Confirmation user={user} />
            </Route>
            <Route path="/register/:slug">
              <RegisterTenant />
            </Route>
            <HostRoute path="/terms">
              <Terms />
            </HostRoute>

            {/* <Route path="/dsgvo" children={<Gdpr />} /> */}
            <Route path="/sign">
              <DocumentSign />
            </Route>
            <Route path="/signed-document">
              <SignedDocumentDownload />
            </Route>
            <Route path="/share">
              <DocumentShare />
            </Route>
            <Route path="/file">
              <FileShare />
            </Route>
            <Route path="/embed">
              <DocumentEmbed />
            </Route>
            <HostRoute path="/privacy">
              {/* External redirect only for host */}
              {() => {
                window.location.href = 'https://www.dieter-datenschutz.de/datenschutz/';
                return null;
              }}
            </HostRoute>
            <HostRoute path="/imprint">
              {() => {
                window.location.href = 'https://www.dieter-datenschutz.de/impressum/';
                return null;
              }}
            </HostRoute>

            <PrivateRoute path="/password">
              <Password />
            </PrivateRoute>
            <PrivateRoute path="/mfa">
              <MFASetup />
            </PrivateRoute>

            <PrivateRoute path="/docs/:questionnaireId">
              <Questionnaire />
            </PrivateRoute>
            <PrivateRoute path="/admin">
              <Admin user={user} />
            </PrivateRoute>
            <PrivateRoute path="/dpo">
              <Dpo />
            </PrivateRoute>
            <PrivateRoute path="/stats">
              <Stats />
            </PrivateRoute>
            <PrivateRoute path="/whitelabel">
              <WhitelabelConfigForm />
            </PrivateRoute>
            <PrivateRoute path="/userconfig">
              <UserConfiguration />
            </PrivateRoute>
            <PrivateRoute path="/usermanagement">
              <UserManagement />
            </PrivateRoute>
            {/* Sprung nach Klaus ohne neuen Login (Addendum v1.5); löst ein authtoken selbst ein. */}
            <Route path="/zu-klaus">
              <KlausHandoff />
            </Route>
            <PrivateRoute path="/klaus">
              <KlausLink />
            </PrivateRoute>
            <PrivateRoute path="/tenant-registrations">
              <TenantRegistrations />
            </PrivateRoute>
            <PrivateRoute path="/chattest">
              <ChatTest />
            </PrivateRoute>
            <PrivateRoute path="/print/:docId">
              <Print />
            </PrivateRoute>
            <PrivateRoute path="/scan-result-print/:propertyId">
              <ScanResultPrint />
            </PrivateRoute>
            <PrivateRoute path="/scan-result-report/:propertyId">
              <ScanResultReport />
            </PrivateRoute>
            <PrivateRoute path="/glossary/:glossaryItem">
              <Glossary />
            </PrivateRoute>
            <PrivateRoute path="/dashboard">
              <Dashboard />
            </PrivateRoute>
            <PrivateRoute path="/">
              <RootRedirect />
            </PrivateRoute>
            <Route path="*">
              <Error />
            </Route>
          </Switch>
        )}
      </ErrorBoundary>
      <ChatWidget />
      <CelloAttribution />
    </TextReplacer>
  );
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.

interface PrivateRouteProps extends RouteProps {
  hostOnly?: boolean;
}

function PrivateRoute({ children, hostOnly, ...rest }: PrivateRouteProps) {
  const { t, i18n } = useTranslation();
  const { user, userLoading } = useUser();
  const history = useHistory();
  // find out if query parameter "token" is set
  const query = useQueryParams();
  const token = query.get('token') || '';
  const authtoken = query.get('authtoken') || '';

  // get mittwald parameters
  const mittwaldInstanceId = query.get('mwinstance');
  const mittwaldUserId = query.get('mwuserid');
  const mittwaldRetrievalKey = query.get('mwkey');

  const [login, { loading: loginLoading }] = useUserLoginMutation();
  const [loginMittwald, { loading: loginMittwaldLoading }] = useUserLoginMittwaldMutation();
  const [logout, { loading: logoutLoading }] = useUserLogoutMutation({ skipRedirect: true });
  const { enqueueSnackbar } = useSnackbar();
  const { data: shareTokenJWT, loading: tokenLoading } = useGetShareTokenJWTQuery({
    variables: { id: token },
  });

  const loading = tokenLoading || loginLoading || loginMittwaldLoading || logoutLoading || userLoading;
  // ?company=<id> from a Klaus deeplink: switch to that company first.
  const companyDeeplink = useCompanyDeeplink(user);

  const jwt = shareTokenJWT?.getShareTokenJWT?.jwt;

  // Get first two letters of language code
  const langString = i18n.language.slice(0, 2) as Locales;

  useEffect(() => {
    // If authtoken is present, use it to log in the user
    if (authtoken && authtoken !== 'without') {
      // If user is already logged in, logout first to prevent state pollution
      const loginWithAuthtoken = () => {
        login({
          variables: {
            email: '',
            password: '',
            token: authtoken,
            locale: langString,
          },
        })
          .then(() => {
            // Remove authtoken from URL without reload - state cleanup handled by logout mutation
            const url = new URL(window.location.href);
            url.searchParams.delete('authtoken');
            // window.history.replaceState({}, '', url.toString());
          })
          .catch((e) =>
            // TODO/WM/ASK: it will be better for "multi lang" to map error codes with messages
            enqueueSnackbar(e.message, { variant: 'error', stack: e.stack })
          );
      };

      if (user) {
        logout().then(loginWithAuthtoken);
      } else {
        loginWithAuthtoken();
      }
    }
  }, [authtoken]);

  useEffect(() => {
    // If mittwald parameters are present, use them to log in the user
    if (mittwaldInstanceId && mittwaldUserId && mittwaldRetrievalKey) {
      logout().then(() => {
        loginMittwald({
          variables: {
            userId: mittwaldUserId,
            instanceId: mittwaldInstanceId,
            retrievalKey: mittwaldRetrievalKey,
          },
        })
          .then()
          .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
      });
    }
  }, [mittwaldInstanceId, mittwaldUserId, mittwaldRetrievalKey]);

  // Host gating: if hostOnly specified and current navigation context is not dieter host, render 404-like error
  const { navigation } = useNavigation();

  if (hostOnly && !navigation.isDieterHost) {
    return <Redirect to="/" />;
  }

  return loading ? (
    <>
      <div className="flex items-center justify-center mt-24">
        <Loading />
      </div>
    </>
  ) : token ? (
    <Route {...rest}>
      {jwt ? (
        children
      ) : (
        <Error error={{ name: '', message: t('common.error.invalid_auth_token', 'Ungültiger Zugriffstoken') }} />
      )}
    </Route>
  ) : (
    <Route {...rest}>
      {!user ? (
        <Login />
      ) : !user.mfaVerified ? (
        <MFAVerify />
      ) : (
        // ) : !user.confirmed && !user.isAnonymous ? (
        //   <Confirmation user={user} />
        // ) : (user.confirmed && !user.conditionsConsent) || ((!user.email || !user.hasPassword) && !onboardingAppId) ? (
        //   <Password />
        (companyDeeplink ?? children)
      )}
    </Route>
  );
}

// Public host gated route (non-auth or simple auth independent) preserving route list stability
interface HostRouteProps extends RouteProps {
  hostOnly?: boolean; // kept for symmetry; if false behaves like Route
}

function HostRoute({ children, hostOnly = true, ...rest }: HostRouteProps) {
  const { navigation } = useNavigation();
  if (hostOnly && !navigation.isDieterHost) {
    return <Redirect to="/" />;
  }
  return <Route {...rest}>{children}</Route>;
}
